/**
 * ErrorBanners — inline banners for failed/rejected files.
 * Shown between the FilesCard and the dropzone.
 *
 * Visual redesign: calm blue "action needed" treatment (no red/alarm), a status
 * chip, an action headline (replacing the filename), the reason, and two stacked
 * actions — "See what I uploaded" (lightweight image of the upload) and
 * "Submit for review". Uploading itself stays with the existing file control.
 */
import { JSX, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, useTheme } from '@mui/material/styles';
import type { RenterFileListItem } from 'src/types/aiFileUploadV2';
import { errorCopyLayout, renderErrorCopy } from '../utils/renderErrorCopy';
import ZoomControls from '../../shared/ZoomControls';
import { GENERIC_ERROR_COPY } from '../errorMessages';
import {
  escalateAIFile,
  getRenterFilePreview,
} from 'src/services/aiFileUploadV2';

interface ErrorBannersProps {
  files: RenterFileListItem[];
  /**
   * ENG-4086: called when the renter chooses to reuse a document that already
   * lives on another member's survey. Omitted by surfaces that cannot link
   * (the banner then renders the stored copy with no action).
   */
  onReuseCrossMember?: (args: {
    sourceSurveyFileId: number;
    targetQitId: number;
  }) => Promise<void>;
}

/**
 * Dismissal key. A blocked re-upload reuses the same RenterFile id (the
 * canonical row), so keying only by id would keep the banner hidden forever
 * after a dismiss. `attempt_count` increments on every blocked re-upload, so a
 * new attempt produces a new key and the banner shows again.
 */
function dismissKey(file: RenterFileListItem): string {
  return `${file.id}:${file.attempt_count ?? 0}`;
}

interface PreviewState {
  file: RenterFileListItem;
  urls: string[];
  loading: boolean;
  failed: boolean;
}

export function ErrorBanners({
  files,
  onReuseCrossMember,
}: ErrorBannersProps): JSX.Element | null {
  const theme = useTheme();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  // Optimistic UI only: confirms immediately after a successful POST and is
  // reconciled by the server's `user_escalated` field on the next WS patch.
  const [optimisticEscalated, setOptimisticEscalated] = useState<Set<number>>(
    new Set()
  );
  const [escalating, setEscalating] = useState<Set<number>>(new Set());
  const [escalateErrors, setEscalateErrors] = useState<Set<number>>(new Set());
  // ENG-4086 cross-member reuse: per-file busy + inline error, never a snackbar.
  const [reusing, setReusing] = useState<Set<number>>(new Set());
  const [reuseErrors, setReuseErrors] = useState<Set<number>>(new Set());
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [zoom, setZoom] = useState(1);

  const handleEscalate = async (file: RenterFileListItem): Promise<void> => {
    setEscalateErrors((prev) => {
      const next = new Set(prev);
      next.delete(file.id);
      return next;
    });
    setEscalating((prev) => new Set(prev).add(file.id));
    try {
      await escalateAIFile(file.id);
      setOptimisticEscalated((prev) => new Set(prev).add(file.id));
    } catch {
      // keep the button enabled so the user can retry
      setEscalateErrors((prev) => new Set(prev).add(file.id));
    } finally {
      setEscalating((prev) => {
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
    }
  };

  const handlePreview = async (file: RenterFileListItem): Promise<void> => {
    setZoom(1);
    setPreview({ file, urls: [], loading: true, failed: false });
    try {
      const { urls } = await getRenterFilePreview(file.id);
      setPreview((prev) =>
        prev && prev.file.id === file.id
          ? { ...prev, urls, loading: false }
          : prev
      );
    } catch {
      setPreview((prev) =>
        prev && prev.file.id === file.id
          ? { ...prev, loading: false, failed: true }
          : prev
      );
    }
  };

  const handleReuse = async (
    file: RenterFileListItem,
    sourceSurveyFileId: number,
    targetQitId: number,
  ): Promise<void> => {
    setReuseErrors((prev) => {
      const next = new Set(prev);
      next.delete(file.id);
      return next;
    });
    setReusing((prev) => new Set(prev).add(file.id));
    try {
      await onReuseCrossMember?.({ sourceSurveyFileId, targetQitId });
      // The link is now a real tile, so the failure banner has done its job.
      setDismissed((prev) => new Set(prev).add(dismissKey(file)));
    } catch {
      setReuseErrors((prev) => new Set(prev).add(file.id));
    } finally {
      setReusing((prev) => {
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
    }
  };

  const errorFiles = useMemo(
    () =>
      files
        .filter(
          (f) =>
            (f.status === 'failed' || f.status === 'rejected') &&
            (f.error_detail != null || f.error_message != null) &&
            !dismissed.has(dismissKey(f))
        )
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ),
    [files, dismissed]
  );

  const bannerSx = {
    background: alpha(theme.palette.info.light, 0.12),
    border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
    borderLeft: `4px solid ${theme.palette.info.main}`,
    borderRadius: '10px',
    padding: '10px 12px',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  };

  if (errorFiles.length === 0) {
    return null;
  }

  return (
    <Box>
      {errorFiles.map((file) => {
        // One headline (the original error's); the body carries the duplicate
        // copy and then the original error's body.
        const { headline, body } = errorCopyLayout(file);
        const headlineText = headline || 'This document needs a new copy';
        const bodyText = body || (headline ? '' : GENERIC_ERROR_COPY);
        const detail = file.error_detail;
        const crossMemberSourceId =
          typeof detail?.source_survey_file_id === 'number'
            ? detail.source_survey_file_id
            : null;
        const crossMemberTargetQitId = file.target_qit_id;
        const crossMemberReady =
          detail?.cross_member === true &&
          crossMemberSourceId !== null &&
          crossMemberTargetQitId !== null &&
          onReuseCrossMember != null;
        return (
          <Box key={file.id} sx={bannerSx} data-testid="error-banner">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: theme.palette.info.dark,
                    background: alpha(theme.palette.info.main, 0.16),
                    borderRadius: 999,
                    px: 1,
                    py: 0.25,
                    fontSize: 10,
                  }}
                >
                  Invalid document
                </Typography>
                <IconButton
                  size="small"
                  aria-label={`Dismiss error for ${file.original_filename}`}
                  onClick={() =>
                    setDismissed((prev) => {
                      const next = new Set(prev);
                      next.add(dismissKey(file));
                      return next;
                    })
                  }
                  sx={{
                    color: theme.palette.info.dark,
                    p: 0.25,
                    flexShrink: 0,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
              {crossMemberReady ? (
                <Typography
                  sx={{
                    fontSize: 12.5,
                    mt: 1,
                    lineHeight: 1.5,
                    color: theme.palette.info.dark,
                  }}
                >
                  This document is already on{' '}
                  <strong>
                    {detail?.member_name ?? 'another household member'}
                  </strong>
                  &apos;s answer to{' '}
                  <strong>{detail?.question ?? 'a question'}</strong>.
                </Typography>
              ) : (
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: theme.palette.info.dark,
                  mt: 1,
                }}
              >
                {renderErrorCopy(headlineText)}
              </Typography>
              )}
              {!crossMemberReady && bodyText ? (
                <Typography
                  sx={{
                    fontSize: 11,
                    mt: 0.75,
                    lineHeight: 1.5,
                    color: theme.palette.info.dark,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {renderErrorCopy(bodyText)}
                </Typography>
              ) : null}
              {crossMemberReady ? (
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.75,
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    disabled={reusing.has(file.id)}
                    onClick={() =>
                      handleReuse(
                        file,
                        crossMemberSourceId as number,
                        crossMemberTargetQitId as number,
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      fontSize: 11.5,
                      bgcolor: theme.palette.info.main,
                      '&:hover': { bgcolor: theme.palette.info.dark },
                    }}
                    data-testid={`reuse-cross-member-${file.id}`}
                  >
                    {reusing.has(file.id) ? 'Adding\u2026' : 'Use it for me too'}
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handlePreview(file)}
                    sx={{
                      p: 0,
                      minWidth: 0,
                      textTransform: 'none',
                      fontSize: 11.5,
                      color: theme.palette.info.main,
                      textDecoration: 'underline',
                    }}
                  >
                    See what I uploaded
                  </Button>
                  {reuseErrors.has(file.id) && (
                    <Typography
                      variant="caption"
                      role="alert"
                      sx={{
                        mt: 0.25,
                        display: 'block',
                        color: theme.palette.error.dark,
                      }}
                    >
                      We could not add it here. Try uploading it again.
                    </Typography>
                  )}
                </Box>
              ) : file.user_escalated || optimisticEscalated.has(file.id) ? (
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: 'text.secondary', display: 'block' }}
                >
                  We&rsquo;ll submit this for review by a compliance specialist.
                </Typography>
              ) : (
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.75,
                  }}
                >
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handlePreview(file)}
                    sx={{
                      p: 0,
                      minWidth: 0,
                      textTransform: 'none',
                      fontSize: 11.5,
                      color: theme.palette.info.main,
                      textDecoration: 'underline',
                    }}
                  >
                    See what I uploaded
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={escalating.has(file.id)}
                    onClick={() => handleEscalate(file)}
                    sx={{
                      textTransform: 'none',
                      fontSize: 11.5,
                      bgcolor: theme.palette.info.main,
                      '&:hover': { bgcolor: theme.palette.info.dark },
                    }}
                  >
                    {escalating.has(file.id)
                      ? 'Submitting…'
                      : 'Submit for review'}
                  </Button>
                  {escalateErrors.has(file.id) && (
                    <Typography
                      variant="caption"
                      role="alert"
                      sx={{
                        mt: 0.25,
                        display: 'block',
                        color: theme.palette.error.dark,
                      }}
                    >
                      We couldn&rsquo;t submit this for review. Please try
                      again.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}

      <Dialog
        open={preview != null}
        onClose={() => setPreview(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, pr: 6 }}>
          What you uploaded
        </DialogTitle>
        <DialogContent
          sx={{
            position: 'relative',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              overflow: 'auto',
              maxHeight: '70vh',
              alignItems: 'center',
            }}
          >
            {preview?.loading ? (
              <CircularProgress size={28} sx={{ my: 3 }} />
            ) : preview && preview.urls.length > 0 ? (
              preview.urls.map((url, index) => (
                <Box
                  key={index}
                  component="img"
                  src={url}
                  alt={`${preview.file.original_filename} page ${index + 1}`}
                  loading="lazy"
                  sx={{
                    width: `${zoom * 100}%`,
                    maxWidth: 'none',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ my: 3 }}>
                We can&rsquo;t show a preview for this document.
              </Typography>
            )}
          </Box>
          {!preview?.loading && preview && preview.urls.length > 0 ? (
            <ZoomControls zoom={zoom} onChange={setZoom} />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPreview(null)}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ErrorBanners;
